import {
  ChangeDetectorRef,
  Component,
  Injector,
  OnInit,
  forwardRef,
  inject
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { _HttpClient, SettingsService } from '@delon/theme';
import { PostcodeLookupService } from '@shared-feature/services/postcode-lookup.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { FormService } from 'src/app/core/util/form.service';
import { SupplierService } from 'src/app/routes/ap/supplier/supplier.service';
import { DataBaseService } from 'src/app/shared-feature/services/_base/data-base.service';
import { DocBaseService } from 'src/app/shared-feature/services/_base/doc-base.service';
import { EinvValidatorsService } from 'src/app/shared-feature/services/einv-validators.service';

@Component({
  selector: 'app-supplier-info-modal',
  templateUrl: './supplier-info-modal.component.html',
  standalone: false,
  providers: [
    {
      provide: DataBaseService,
      useExisting: forwardRef(() => SupplierService)
    },
    {
      provide: SupplierService,
      useFactory: (injector: Injector) => new SupplierService(injector),
      deps: [Injector]
    }
  ]
})
export class SupplierInfoModalComponent implements OnInit {
  private http = inject(_HttpClient);
  private cdr = inject(ChangeDetectorRef);
  private einvValidators = inject(EinvValidatorsService);
  private fb = inject(FormBuilder);
  private fs = inject(FormService);
  private msg = inject(NzMessageService);
  supplierNameField = '';
  birClassification = 0;
  waCategory = 0;
  phTinDisplay = '';
  doctype = '';
  isVisible = false;
  editing = false;
  validating = false;
  hideZipCodeField = false;
  isZipcodeReq = false;
  eInvoiceControl = 0;
  excludedDocTypes = ['PR', 'PO', 'GRN'];
  canUpdate = false;
  allowUpdateDocTypes = ['CRF', 'PINV', 'PDN', 'PCN'];

  form = this.createForm();

  get country() {
    return this.settings.user['company']?.country;
  }

  constructor(
    public dataSrv: DocBaseService,
    private settings: SettingsService,
    private modal: NzModalService,
    private postcodeLookupSrv: PostcodeLookupService
  ) {}

  ngOnInit(): void {
    this.form;
  }

  createForm() {
    const form = this.fb.group({
      supplierId: ['', []],
      supplierName: ['', [Validators.maxLength(250)]],
      phoneNo: ['', [Validators.maxLength(100)]],
      email: ['', [Validators.email, Validators.maxLength(300)]],
      tinType: [<any>null, [Validators.required]],
      tinNumber: ['', [Validators.maxLength(14)]],
      registrationNumber: ['', [Validators.maxLength(40)]],
      salesTaxRegNo: ['', [Validators.maxLength(35)]],
      serviceTaxRegNo: ['', [Validators.maxLength(35)]],
      msicCode: ['', [Validators.maxLength(10)]],
      billingAddress: ['', [Validators.maxLength(400)]],
      address1: ['', [Validators.maxLength(300)]],
      address2: ['', [Validators.maxLength(300)]],
      address3: ['', [Validators.maxLength(100)]],
      address4: ['', [Validators.maxLength(100)]],
      zipCode: ['', [Validators.maxLength(20)]],
      city: ['', [Validators.maxLength(50)]],
      state: ['', [Validators.maxLength(100)]],
      country: ['', [Validators.maxLength(100)]],
      isSelfBilled: [false, []]
    });

    form.controls.country.valueChanges.subscribe(val => {
      if (!this.dataSrv.observeChanges) return;

      // if (val === 'MY') {
      //   if (this.eInvoiceControl == 2 && !this.excludedDocTypes.includes(this.doctype)) {
      //     this.isZipcodeReq = true;
      //   }
      //   this.amendValidators();
      // }
    });

    form.controls.tinType.valueChanges.subscribe(val => {
      if (this.country == 'MY') this.hideZipCodeField = val === 3 || val === 4;

      if (!this.dataSrv.observeChanges) return;
      this.updateRegNoAndTinVallidation();
      this.amendValidators();
    });

    form.controls.registrationNumber.valueChanges.subscribe(() => {
      if (!this.dataSrv.observeChanges) return;
      this.updateRegNoAndTinVallidation();
    });

    form.controls.tinNumber.valueChanges.subscribe(() => {
      if (!this.dataSrv.observeChanges) return;
      this.updateRegNoAndTinVallidation();
    });

    // Postcode lookup for main form
    form.controls.zipCode.valueChanges.subscribe(val => {
      if (!this.dataSrv.observeChanges) return;
      this.lookupAndFillAddress(val, form.controls.city, form.controls.state);
    });

    form.valueChanges.subscribe(() => {
      if (!this.dataSrv.observeChanges) return;
      this.dataSrv.modified = true;
    });
    return form;
  }

  show(_: any, doctype: string) {
    //this.data = data;
    this.canUpdate = this.allowUpdateDocTypes.includes(doctype);
    this.validating = false;
    this.birClassification = 0;
    this.phTinDisplay = '';
    this.waCategory = 0;
    const data = this.dataSrv.form.getRawValue();
    if (data.supplier) {
      if (this.country === 'PH') {
        this.http
          .get(`api/suppliers/${data.supplier.id}/BirClassification`)
          .subscribe((bir: any) => {
            if (bir) {
              this.birClassification = bir.classification;
              this.waCategory = bir.waCategory;
            }
          });
      }

      // Set the display of RegNo as RegistrationNo (tinBranchCode)
      if (data.supplier.tinBranchCode && data.supplier.registrationNo)
        this.phTinDisplay = `${data.supplier.registrationNo} (${data.supplier.tinBranchCode})`;
      else if (data.supplier.registrationNo)
        this.phTinDisplay = data.supplier.registrationNo;
      else if (data.supplier.tinBranchCode)
        this.phTinDisplay = data.supplier.tinBranchCode;
      else this.phTinDisplay = '';
    }

    this.readData(data);
    this.doctype = doctype;
    this.isVisible = true;

    if (!this.excludedDocTypes.includes(doctype) && 'eInvoiceControl' in data) {
      this.einvValidators.toggleFormValidators(this.form, data.eInvoiceControl);
    }

    this.editing = this.dataSrv.editing;
    if (!this.dataSrv.editing) this.form.disable();
    else this.form.enable();
  }

  private readData(data: any) {
    const value = { ...data };
    // Ensure phoneNo is set from phone1 if phoneNo is missing (backward compatibility)
    if (!value.phoneNo && value.phone1) value.phoneNo = value.phone1;

    this.supplierNameField = '';
    if ('billFrom' in data) {
      value.supplierName = data.billFrom;
      this.supplierNameField = 'billFrom';
    } else if ('dnFrom' in data) {
      value.supplierName = data.dnFrom;
      this.supplierNameField = 'dnFrom';
    } else if ('cnFrom' in data) {
      value.supplierName = data.cnFrom;
      this.supplierNameField = 'cnFrom';
    } else if ('receiveFrom' in data) {
      // Supplier Refund
      value.supplierName = data.receiveFrom;
      this.supplierNameField = 'receiveFrom';
    } else if ('payTo' in data) {
      // Pay Bill
      value.supplierName = data.payTo;
      this.supplierNameField = 'payTo';
    }

    if ('eInvoiceControl' in data) {
      this.eInvoiceControl = data.eInvoiceControl;
    }

    this.readAddress(value);
    this.form.setErrors(null);
    this.form.patchValue(value);
    if (this.country === 'MY' && data.country == null) {
      this.form.controls.country.patchValue('MY');
    }
  }

  cancel() {
    this.isVisible = false;
  }

  get regNoLabel(): string {
    switch (this.form.controls['tinType'].value) {
      case 2:
        return 'Identification No.';
      case 3:
      case 4:
        return 'Passport No.';
      default:
        return 'Registration No.';
    }
  }

  save(updateSuppProfile: boolean = false): void {
    this.insertDefaultValue();
    // Check if both regNo and tinNo are set to default values, if so, block save
    if (this.form.hasError('bothDefaultValues')) {
      this.msg.error(
        `Both ${this.regNoLabel} and TIN Number cannot be default values.`
      );
      return;
    }

    if (!this.dataSrv.editing) return;
    console.log(this.form.getRawValue());
    this.fs.validateAsync(this.form).then(isValid => {
      this.validating = false;
      this.cdr.detectChanges();
      if (!isValid) {
        const invalidControls = this.fs.getInvalidControls(this.form);
        this.fs.showValidationMsg(invalidControls);
        return;
      }

      const value: any = { ...this.form.getRawValue() };

      this.splitAddress(value);

      if (value.email === '') {
        value.email = null;
      }

      if (this.supplierNameField)
        value[this.supplierNameField] = value.supplierName;

      this.dataSrv.form.patchValue(value);

      if (updateSuppProfile) {
        this.form.controls.supplierId.setValidators([Validators.required]);
        setTimeout(() => {
          this.http
            .post('api/suppliers/UpdateSupplierProfile', value)
            .subscribe({
              next: () => {
                this.msg.success('Supplier profile updated successfully.');
                this.dataSrv.modified = true;
              },
              error: err => {
                this.msg.error(
                  `Failed to update supplier profile: ${err.message}`
                );
              }
            });
        }, 500);
      }

      this.isVisible = false;
      return;
    });
  }

  insertDefaultValue(): void {
    const regNo = this.form.controls.registrationNumber;
    const tinNo = this.form.controls.tinNumber;
    const tinType = this.form.controls.tinType.value ?? -1;

    const defaultValues: { [key: number]: { tinNo: string; regNo: string } } = {
      2: { tinNo: 'EI00000000010', regNo: '000000000000' },
      3: { tinNo: 'EI00000000030', regNo: 'NA' },
      4: { tinNo: 'EI00000000030', regNo: 'NA' }
    };

    const defaults = defaultValues[tinType] || null;
    const data = this.dataSrv.form.getRawValue();
    if ('eInvoiceControl' in data) {
      if (data.eInvoiceControl === 2) {
        if (defaults) {
          // Assign default values
          if (
            !regNo.value ||
            regNo.value.trim() === '' ||
            regNo.value === 'NA' ||
            regNo.value === '000000000000'
          ) {
            this.form.controls.registrationNumber.setValue(defaults.regNo);
          }
          if (
            !tinNo.value ||
            tinNo.value.trim() === '' ||
            ['EI00000000010', 'EI00000000030', 'NA'].includes(tinNo.value)
          ) {
            this.form.controls.tinNumber.setValue(defaults.tinNo);
          }
        } else {
          // Clear default values
          if (['NA', '000000000000'].includes(regNo.value ?? '')) {
            this.form.controls.registrationNumber.setValue(null);
          }
          if (
            ['EI00000000010', 'EI00000000030', 'NA'].includes(tinNo.value ?? '')
          ) {
            this.form.controls.tinNumber.setValue(null);
          }
        }
      }
      this.einvValidators.toggleFormValidators(this.form, data.eInvoiceControl);
    }
  }

  private splitAddress(data: any) {
    // Avoid null data
    if (!data.billingAddress) data.billingAddress = '';

    // Split address lines by line break
    const lines = data.billingAddress.split('\n');

    // If address less than 4 lines, fill the rest with empty string
    while (lines.length < 4) lines.push('');

    data.address1 = lines[0];
    data.address2 = lines[1];
    data.address3 = lines[2];
    data.address4 = lines[3];
  }

  // Join address1,2,3,4 as one address, with line break
  private readAddress(data: any) {
    if (data.billingAddress) return;
    const address = [data.address1, data.address2, data.address3, data.address4]
      .filter(a => a)
      .join('\n');
    data.billingAddress = address;
  }

  public amendValidators() {
    this.dataSrv.observeChanges = false;

    const zipCodeControl = this.form.controls.zipCode;
    const zipValidators = [Validators.maxLength(20)];

    if (this.country === 'MY') {
      zipCodeControl.setValidators(zipValidators);
    } else {
      zipCodeControl.setValidators([Validators.maxLength(20)]);
    }

    zipCodeControl.setValidators(zipValidators);
    zipCodeControl.updateValueAndValidity({ onlySelf: true });
    this.dataSrv.observeChanges = true;
  }

  updateRegNoAndTinVallidation() {
    this.dataSrv.observeChanges = false;
    const regNo = this.form.controls.registrationNumber;
    const tinNo = this.form.controls.tinNumber;

    // Default to -1 if null (might be PH company)
    const tinType = this.form.controls.tinType.value ?? -1;
    const processType = tinType >= 1 && tinType <= 4 ? tinType : -1;
    // const oneOfTheFieldsPopulated = regNo.value || tinNo.value;

    // Check if doctype is excluded
    const isExcludedDoctype = this.excludedDocTypes.includes(this.doctype);

    const tinValidators = (isReq: boolean) =>
      isReq
        ? [Validators.required, Validators.maxLength(14)]
        : [Validators.maxLength(14)];

    const regValidators = (isReq: boolean) =>
      isReq
        ? [Validators.required, Validators.maxLength(40)]
        : [Validators.maxLength(40)];

    switch (processType) {
      case 1:
        regNo.setValidators(
          regValidators(!isExcludedDoctype && this.eInvoiceControl == 2)
        );
        tinNo.setValidators(
          tinValidators(!isExcludedDoctype && this.eInvoiceControl == 2)
        );
        break;
      case 2:
      case 3:
        const regNoDefaultValue = processType === 2 ? '000000000000' : 'NA';
        const tinNoDefaultValue =
          processType === 2 ? 'EI00000000010' : 'EI00000000030';

        const isRegNoDefault = regNo.value === regNoDefaultValue;
        const isTinNoDefault = tinNo.value === tinNoDefaultValue;

        if (this.eInvoiceControl == 2 && isRegNoDefault && isTinNoDefault) {
          this.form.setValidators(
            isBothDefaultValueValidator(regNoDefaultValue, tinNoDefaultValue)
          );
        } else {
          regNo.setValidators(
            regValidators(
              !isExcludedDoctype && !isRegNoDefault && this.eInvoiceControl == 2
            )
          );
          tinNo.setValidators(
            tinValidators(
              !isExcludedDoctype && !isTinNoDefault && this.eInvoiceControl == 2
            )
          );
        }
        break;
      case 4:
        regNo.setValidators(regValidators(false));
        tinNo.setValidators(tinValidators(false));
        break;
    }
    this.dataSrv.observeChanges = true;
  }

  /**
   * Lookup postcode and auto-fill city and state if found
   * @param zipCode The postal code to lookup
   * @param cityControl The form control for city
   * @param stateControl The form control for state
   */
  private lookupAndFillAddress(
    zipCode: string | null,
    cityControl: any,
    stateControl: any
  ): void {
    // Only proceed if zipCode is 5 digits and country is Malaysia
    if (!zipCode || !/^\d{5}$/.test(zipCode)) {
      return;
    }

    // Only auto-fill for Malaysia
    const country = this.form.controls.country?.value;
    if (country && country !== 'MY') {
      return;
    }

    this.postcodeLookupSrv.lookupPostcode(zipCode).subscribe(result => {
      if (result.found) {
        this.dataSrv.observeChanges = false; // Prevent triggering other valueChanges

        // Only update if current values are empty (don't replace existing values)
        if (!cityControl.value) {
          cityControl.setValue(result.city);
        }

        if (!stateControl.value) {
          stateControl.setValue(result.state);
        }

        this.dataSrv.observeChanges = true;
      } else {
      }
    });
  }

  onScanSuccess(response: any) {
    this.form.patchValue({
      supplierName: response.name,
      registrationNumber: response.idNumber,
      email: response.email,
      serviceTaxRegNo: response.sst,
      address1: response.addressLine0,
      address2: response.addressLine1,
      address3: response.addressLine2,
      city: response.city,
      state: response.state,
      country: response.country,
      zipCode: response.postalZone,
      tinNumber: response.tin,
      msicCode: response.msic,
      phoneNo: response.contactNumber
    });

    const billingAddress = [
      response.addressLine0,
      response.addressLine1,
      response.addressLine2
    ]
      .filter(line => line)
      .join('\n');
    this.form.controls.billingAddress.setValue(billingAddress);

    this.cdr.detectChanges();
  }

  updateSuppProfile() {
    this.modal.confirm({
      nzTitle: 'Confirmation',
      nzContent:
        "Are you sure you want to overwrite the existing Supplier Maintenance Information with the transaction's supplier details? This action cannot be undone.",
      nzOkText: 'Overwrite',
      nzCancelText: 'Cancel',
      nzOnOk: () => this.save(true)
    });
  }
}

export function isBothDefaultValueValidator(
  regNoDefaultValue: string,
  tinNoDefaultValue: string
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const regNo = control.get('registrationNo');
    const tinNo = control.get('tinNumber');

    if (!regNo || !tinNo) {
      return null; // Return early if controls are missing
    }

    // Check if both fields are set to their default values
    if (regNo && tinNo) {
      const isRegNoDefault = regNo.value === regNoDefaultValue;
      const isTinNoDefault = tinNo.value === tinNoDefaultValue;

      // If both are default, return an error
      if (isRegNoDefault && isTinNoDefault) {
        return { bothDefaultValues: true }; // Custom error key
      }
    }

    return null; // No error if both are not default
  };
}
